import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { adminAPI } from '../services/api';
import { Shield, AlertCircle } from 'lucide-react';

interface AdminProtectedRouteProps {
  children: React.ReactNode;
}

const AdminProtectedRoute: React.FC<AdminProtectedRouteProps> = ({ children }) => {
  const location = useLocation();
  const [isVerifying, setIsVerifying] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    verifyAdminAccess();
  }, []);

  const verifyAdminAccess = async () => {
    const adminToken = localStorage.getItem('admin_token');
    const expiresAt = localStorage.getItem('admin_expires_at');
    const adminLoggedIn = localStorage.getItem('admin_logged_in');

    // Check if admin token exists
    if (!adminToken || !adminLoggedIn) {
      setIsVerifying(false);
      setIsAuthenticated(false);
      return;
    }

    // Check if token expired
    if (expiresAt && Date.now() / 1000 > parseInt(expiresAt)) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_expires_at');
      localStorage.removeItem('admin_logged_in');
      setIsAuthenticated(false);
      setError('Session expired. Please login again.');
      setIsVerifying(false);
      return;
    }

    try {
      // Verify token by making actual API call
      const response = await adminAPI.getStats();
      
      if (response.status === 200) {
        setIsAuthenticated(true);
      } else {
        // Invalid token
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_expires_at');
        localStorage.removeItem('admin_logged_in');
        setIsAuthenticated(false);
        setError('Session expired. Please login again.');
      }
    } catch (err: any) {
      // Auth failed
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_expires_at');
      localStorage.removeItem('admin_logged_in');
      setIsAuthenticated(false);
      
      if (err.response?.status === 401) {
        setError('Invalid or expired token');
      } else if (err.response?.status === 403) {
        setError('Access denied. IP not whitelisted.');
      } else {
        setError('Failed to verify admin access');
      }
    } finally {
      setIsVerifying(false);
    }
  };

  if (isVerifying) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: '1rem'
      }}>
        <Shield size={48} style={{ color: '#2563eb', animation: 'pulse 2s infinite' }} />
        <h2>Verifying Admin Access...</h2>
        <p style={{ color: '#6b7280' }}>Please wait</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Show error if any, then redirect
    if (error) {
      setTimeout(() => {
        // Redirect after showing error
      }, 2000);
      
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: '1rem',
          padding: '2rem'
        }}>
          <div style={{
            background: '#fee2e2',
            color: '#991b1b',
            padding: '1rem 1.5rem',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            maxWidth: '500px'
          }}>
            <AlertCircle size={24} />
            <div>
              <h3 style={{ margin: 0, marginBottom: '0.25rem' }}>Access Denied</h3>
              <p style={{ margin: 0 }}>{error}</p>
            </div>
          </div>
          <p style={{ color: '#6b7280' }}>Redirecting to login...</p>
        </div>
      );
    }
    
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export default AdminProtectedRoute;